// store/slices/homeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";

export const fetchHomeData = createAsyncThunk(
  "home/fetchHomeData",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const selectedCity = state.city.selectedCity;

      if (!selectedCity?.id) {
        return rejectWithValue("City selection is required");
      }

      const response = await api.getHomeData();
      // If response has data wrapper, use it, otherwise use response directly
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load data");
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  homeData: null,
  // Content Section
  sliders: [],
  heroBanners: [],
  offerBanner: null,
  promotionBanners: [],
  categories: [],
  // Products Section
  popularProducts: [],
  smartPhones: [],
  smartWatches: [],
  smartTablets: [],
  laptops: [],
  reviews: [],
  // Cities
  cities: [],
  // User & App Info
  userInfo: null,
  appInfo: {
    latest_version: "1.0.0",
    force_update: false,
    update_available: false,
    response: true,
    last_updated: null,
  },
  currentQueryParams: {},
};

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    clearHomeData: (state) => {
      Object.assign(state, initialState);
    },
    setQueryParams: (state, action) => {
      state.currentQueryParams = action.payload;
    },
    updateUserInfo: (state, action) => {
      state.userInfo = { ...state.userInfo, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.loading = false;
        state.homeData = action.payload;
        state.currentQueryParams = action.meta.arg;

        const data = action.payload;
        
        // Content Section - match API field names
        if (data.sliders) state.sliders = data.sliders;
        if (data.hero_banners) state.heroBanners = data.hero_banners;
        if (data.offer_banner) state.offerBanner = data.offer_banner;
        if (data.promotion_banners) state.promotionBanners = data.promotion_banners;
        if (data.categories) state.categories = data.categories;
        
        // Products Section - match API field names
        if (data.popular_products) state.popularProducts = data.popular_products;
        if (data.smart_phones) state.smartPhones = data.smart_phones;
        if (data.smart_watches) state.smartWatches = data.smart_watches;
        if (data.smart_tablets) state.smartTablets = data.smart_tablets;
        if (data.laptops) state.laptops = data.laptops;
        if (data.reviews) state.reviews = data.reviews;
        
        // Cities
        if (data.cities) state.cities = data.cities;
        
        // User & App Info
        if (data.user_info) state.userInfo = data.user_info;
        if (data.app_info) state.appInfo = { ...state.appInfo, ...data.app_info };
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearHomeData, setQueryParams, updateUserInfo } = homeSlice.actions;
export default homeSlice.reducer;